import styles from "./progressBar.module.css"

type Props = {
    current: number
    total: number
    unit?: string
    showPercent?: boolean
    precision?: number
    clamp?: boolean
    currentLabel?: string
    totalLabel?: string
    formatValue?: (value: number) => string
    className?: string
}

const defaultFormatValue = (value: number) => {
    if (!Number.isFinite(value)) return "0"
    return Number.isInteger(value) ? value.toString() : value.toString()
}

export const ProgressBar = (props: Props) => {
    const {
        current,
        total,
        unit,
        showPercent = true,
        precision = 1,
        clamp = true,
        currentLabel,
        totalLabel,
        formatValue = defaultFormatValue,
        className
    } = props

    const safeCurrent = Number.isFinite(current) ? current : 0
    const safeTotal = Number.isFinite(total) ? total : 0

    const leftCurrentText = `${formatValue(safeCurrent)}${currentLabel ? ` ${currentLabel}` : ''}`
    const leftTotalText = `${formatValue(safeTotal)}${totalLabel ? ` ${totalLabel}` : ''}`

    const percentRaw = safeTotal > 0 ? (safeCurrent / safeTotal) * 100 : 0
    const percent = clamp
        ? Math.min(100, Math.max(0, percentRaw))
        : percentRaw

    const percentText = percentRaw.toFixed(precision) + "%"
    const leftText = unit ? `${leftCurrentText} / ${leftTotalText} ${unit}` : `${leftCurrentText} / ${leftTotalText}`
    const fillColor = percentRaw > 100
        ? "#e05252"
        : percentRaw > 85
            ? "#f2c94c"
            : "rgba(109, 151, 145, 0.35)"

    return (
        <div
            className={`${styles.container} ${className || ""}`.trim()}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
        >
            <div className={styles.labelRow}>
                <span className={styles.leftLabel}>{leftText}</span>
                {showPercent && <span className={styles.percentLabel}>{percentText}</span>}
            </div>

            <div className={styles.track}>
                <div className={styles.fill} style={{ width: `${percent}%`, backgroundColor: fillColor }} />
            </div>
        </div>
    )
}
